import { Modal as AntModal } from 'antd';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  Button,
  IconType,
  IconVariant,
  IconWrap,
  Loader,
  Typography
} from '../../..';
import { theme } from '../../theme/semanticTokens';

export enum ModalType {
  Warning = 'warning',
  Information = 'information',
  Share = 'share',
  Money = 'money',
  Success = 'success',
  Critical = 'critical'
}
export interface ModalProps {
  open: boolean;
  title: string;
  type?: ModalType;
  children: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  confirmLoading?: boolean;
  loading?: boolean;
  onConfirm?: VoidFunction;
  onCancel?: VoidFunction;
  onClose: VoidFunction;
  width?: number;
  className?: string;
  customFooter?: React.ReactNode;
}

const StyledModal = styled(AntModal)`
  .ant-modal-content {
    padding: ${theme.dime.spacing.xl};
    padding-bottom: ${theme.dime.spacing.l};
  }

  .ant-modal-header {
    margin-bottom: 18px;
  }

  .ant-modal-footer {
    margin-top: ${theme.dime.spacing.xl};
    display: flex;
    justify-content: space-between;
    gap: ${theme.dime.spacing.m};
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.m};
`;

const SpinningContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
`;

const StyledIcon = styled(IconWrap)<{ $modalType: ModalType }>`
  color: ${({ $modalType }) => {
    switch ($modalType) {
      case ModalType.Warning:
        return theme.dime.colors.icon.warning;
      case ModalType.Information:
        return theme.dime.colors.icon.info;
      case ModalType.Share:
        return theme.dime.colors.icon.secondary;
      case ModalType.Money:
      case ModalType.Success:
        return theme.dime.colors.icon.success;
      case ModalType.Critical:
        return theme.dime.colors.icon.critical;
      default:
        return theme.dime.colors.icon.info;
    }
  }};
`;

export const Modal: React.FC<ModalProps> = ({
  open,
  title,
  type = ModalType.Information,
  children,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmDisabled = false,
  cancelDisabled = false,
  confirmLoading = false,
  loading = false,
  onConfirm,
  onCancel,
  onClose,
  width = 520,
  className,
  customFooter
}) => {
  const getIconType = useMemo(
    () =>
      (modalType: ModalType): IconType => {
        switch (modalType) {
          case ModalType.Warning:
            return IconType.Warning;
          case ModalType.Information:
            return IconType.Information;
          case ModalType.Share:
            return IconType.Linking;
          case ModalType.Money:
            return IconType.DMLinking;
          case ModalType.Success:
            return IconType.Approved;
          case ModalType.Critical:
            return IconType.Declined;
          default:
            return IconType.Information;
        }
      },
    []
  );

  const iconType = getIconType(type);
  const hasFooter = !!customFooter || !!onCancel || !!onConfirm;
  const footer =
    customFooter ||
    (hasFooter && (
      <>
        {!!onCancel && (
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={cancelDisabled}
          >
            {cancelText}
          </Button>
        )}
        {!!onConfirm && (
          <Button
            onClick={onConfirm}
            loading={confirmLoading}
            disabled={confirmDisabled}
          >
            {confirmText}
          </Button>
        )}
      </>
    ));

  const customHeader = (
    <HeaderContainer>
      <TitleContainer>
        <StyledIcon
          type={iconType}
          variant={IconVariant.Solid}
          $modalType={type}
        />
        <Typography variant="heading3Bold">{title}</Typography>
      </TitleContainer>
      <Button
        variant="secondary"
        onClick={onClose}
        iconOnly={IconType.Close}
        size="middle"
      />
    </HeaderContainer>
  );

  return (
    <StyledModal
      title={customHeader}
      open={open}
      onCancel={onClose}
      footer={footer}
      width={width}
      className={className}
      closable={false}
      centered
    >
      {loading ? (
        <SpinningContainer>
          <Loader />
        </SpinningContainer>
      ) : (
        children
      )}
    </StyledModal>
  );
};
